import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, ChevronUp, X, ExternalLink } from 'lucide-react';
import { useMessages } from '../context/MessagesContext';
import { useAuth } from '../context/AuthContext';
import ChatWindow from './messaging/ChatWindow';
import { useMessaging } from '../hooks/useMessaging';
import useMessengerCalls from '../hooks/useMessengerCalls';

const API_URL = window.__BACKEND_URL__;

/**
 * FloatingMessenger — platform-aware Messenger dock.
 *
 * Desktop (≥1024px)
 * - Shows a compact Messenger stack of up to 3 ChatWindow panels.
 * - Keeps the pop-out clear of the viewport edge.
 * - Uses the improved ChatWindow so the floating pop-out has parity
 *   with the full Messenger thread: attachments, ticks, retry,
 *   planner, nudge, mute, archive, delete, block and request handling.
 *
 * Mobile (<1024px)
 * - Uses one launcher pill above the mobile toolbar.
 * - Expands into a safe full-screen Messenger sheet.
 * - Shows one active conversation at a time, with optional tabs when
 *   more than one floating conversation is open.
 * - Keeps the composer locked inside the viewport and avoids clashing
 *   with mobile nav/footer.
 *
 * Preferences (on/off + desktop corner) come from:
 * /api/account/messenger-preferences
 */
export default function FloatingMessenger() {
  const { user } = useAuth();
  const location = useLocation();

  const {
    windows,
    conversations,
    closeConversation,
    minimiseConversation,
    openConversation,
    totalUnread,
  } = useMessages();

  const [prefs, setPrefs] = useState({
    floating_enabled: true,
    floating_position: 'bottom-right',
  });

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false,
  );

  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [activeMobileConversationId, setActiveMobileConversationId] = useState(null);

  const isFullMessengerRoute = location.pathname.startsWith('/messages');
  const floatingCallsEnabled = String(
    process.env.REACT_APP_ENABLE_MESSENGER_CALLS || '',
  ).toLowerCase() === 'true';

  // One user-level socket feeds every floating conversation and its call
  // signalling. Creating a socket per pop-out would make the backend's
  // single-user connection manager disconnect the other windows.
  const floatingWsHolder = useRef(null);
  const floatingCallsApi = useMessengerCalls({
    getWs: () => floatingWsHolder.current?.current || null,
    currentUserId: user?.id,
    axios,
    API: API_URL,
    AX: { withCredentials: true },
  });
  const {
    newMessage: floatingRealtimeEvent,
    typingUsers: floatingTypingUsers,
    wsState: floatingWsState,
    wsRef: floatingWsRef,
  } = useMessaging(null, {
    enabled: !!user && !isFullMessengerRoute,
    onSignal: floatingCallsApi.handleSignal,
  });
  floatingWsHolder.current = floatingWsRef;

  const getConversationTitle = useCallback(
    (conversationId) => {
      const convo = conversations.find((c) => c.id === conversationId);

      if (!convo) return 'Messages';
      if (convo.is_group) return convo.title || 'Group chat';

      const peer = (convo.participants || []).find((p) => p.user_id !== user?.id);
      return peer?.name || peer?.email || 'Conversation';
    },
    [conversations, user?.id],
  );

  const openFullMessenger = useCallback(
    (conversationId = activeMobileConversationId) => {
      if (conversationId) {
        window.location.href = `/messages/${conversationId}`;
      } else {
        window.location.href = '/messages';
      }
    },
    [activeMobileConversationId],
  );

  // Track viewport size so we can switch dock → mobile sheet cleanly.
  useEffect(() => {
    const onResize = () => {
      const nextIsMobile = window.innerWidth < 1024;
      setIsMobile(nextIsMobile);

      // When returning to desktop, collapse the mobile sheet state so the
      // next mobile open starts cleanly.
      if (!nextIsMobile) setMobileExpanded(false);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Pull Messenger preferences on auth + react to live settings changes.
  useEffect(() => {
    if (!user) return undefined;

    let alive = true;

    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/account/messenger-preferences`,
          { withCredentials: true },
        );

        if (!alive) return;

        setPrefs({
          floating_enabled: data.floating_enabled !== false,
          floating_position: data.floating_position || 'bottom-right',
        });
      } catch {
        // Defaults are safe.
      }
    })();

    const onPrefs = (e) => {
      setPrefs((current) => ({
        ...current,
        ...(e.detail || {}),
      }));
    };

    window.addEventListener('messenger-prefs-changed', onPrefs);

    return () => {
      alive = false;
      window.removeEventListener('messenger-prefs-changed', onPrefs);
    };
  }, [user]);

  // Keep active mobile conversation valid as windows open/close.
  useEffect(() => {
    if (!windows.length) {
      setMobileExpanded(false);
      setActiveMobileConversationId(null);
      return;
    }

    const stillOpen = windows.some((windowItem) => windowItem.id === activeMobileConversationId);

    if (!stillOpen) {
      setActiveMobileConversationId(windows[windows.length - 1]?.id || null);
    }
  }, [windows, activeMobileConversationId]);

  // When the first window is opened on mobile, expand the sheet automatically.
  useEffect(() => {
    if (isMobile && windows.length > 0) {
      setMobileExpanded(true);
      setActiveMobileConversationId((current) => current || windows[windows.length - 1]?.id || null);
    }
  }, [isMobile, windows]);

  const activeMobileWindow = useMemo(() => {
    if (!windows.length) return null;

    return (
      windows.find((windowItem) => windowItem.id === activeMobileConversationId) ||
      windows[windows.length - 1] ||
      null
    );
  }, [windows, activeMobileConversationId]);

  const mobileConvoId = activeMobileWindow?.id || null;
  const mobileTitle = mobileConvoId ? getConversationTitle(mobileConvoId) : 'Messages';

  const handleCloseMobileActive = useCallback(() => {
    if (!mobileConvoId) {
      setMobileExpanded(false);
      return;
    }

    closeConversation(mobileConvoId);

    const remaining = windows.filter((windowItem) => windowItem.id !== mobileConvoId);
    const nextId = remaining[remaining.length - 1]?.id || null;

    setActiveMobileConversationId(nextId);

    if (!nextId) {
      setMobileExpanded(false);
    }
  }, [closeConversation, mobileConvoId, windows]);

  // Do not show the floating dock over the full Messenger page. The full
  // Messenger already owns the screen and has its own mobile/full layout.
  if (!user || !prefs.floating_enabled || isFullMessengerRoute) return null;
  if (!windows.length) return null;

  // ------------------------------------------------------------------
  // MOBILE — launcher pill + safe full-screen sheet
  // ------------------------------------------------------------------
  if (isMobile) {
    if (!mobileExpanded) {
      return (
        <button
          type="button"
          onClick={() => setMobileExpanded(true)}
          className="fixed left-1/2 -translate-x-1/2 z-[220] flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0055FF] text-white shadow-xl transition-colors max-w-[calc(100dvw-2rem)]"
          style={{ bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
          data-testid="mobile-messenger-pill"
          aria-label={`Open Messenger: ${mobileTitle}`}
        >
          <MessageSquare className="h-4 w-4 shrink-0" />
          <span className="text-sm font-medium truncate">{mobileTitle}</span>
          <ChevronUp className="h-3.5 w-3.5 shrink-0 opacity-80" />

          {totalUnread > 0 && (
            <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      );
    }

    return (
      <div
        className="fixed inset-0 z-[220] bg-theme flex flex-col animate-in slide-in-from-bottom duration-200"
      data-ccuk-fullscreen-overlay="true"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
        data-testid="mobile-messenger-sheet"
        role="dialog"
        aria-label="Floating Messenger"
      >
        <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b border-theme bg-theme-surface">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center shrink-0">
              <MessageSquare className="h-4 w-4" />
            </div>

            <div className="min-w-0">
              <div className="text-sm font-semibold text-theme-text truncate">
                Messenger
              </div>
              <div className="text-[11px] text-theme-muted truncate">
                {mobileTitle}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => openFullMessenger(mobileConvoId)}
              className="text-theme-muted hover:text-[#002FA7] p-1.5 rounded-lg"
              title="Open full Messenger"
              data-testid="mobile-messenger-open-full"
              aria-label="Open full Messenger"
            >
              <ExternalLink className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={() => setMobileExpanded(false)}
              className="text-theme-muted hover:text-theme-text p-1.5 rounded-lg"
              title="Minimise"
              data-testid="mobile-messenger-minimise"
              aria-label="Minimise Messenger"
            >
              <ChevronUp className="h-4 w-4 rotate-180" />
            </button>

            <button
              type="button"
              onClick={handleCloseMobileActive}
              className="text-theme-muted hover:text-theme-text p-1.5 rounded-lg"
              title="Close"
              data-testid="mobile-messenger-close"
              aria-label="Close Messenger"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Open-thread strip — lets user jump between floating conversations without tiling on mobile. */}
        {windows.length > 1 && (
          <div className="shrink-0 flex gap-1.5 px-2 py-1.5 border-b border-theme overflow-x-auto bg-theme-surface">
            {windows.map((windowItem) => {
              const label = getConversationTitle(windowItem.id);
              const active = windowItem.id === mobileConvoId;

              return (
                <button
                  key={windowItem.id}
                  type="button"
                  onClick={() => {
                    setActiveMobileConversationId(windowItem.id);
                    openConversation(windowItem.id);
                  }}
                  className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    active
                      ? 'bg-[#002FA7] text-white'
                      : 'bg-theme-secondary text-theme-text hover:bg-theme-hover'
                  }`}
                  data-testid={`mobile-messenger-tab-${windowItem.id}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-hidden">
          {mobileConvoId ? (
            <ChatWindow
              conversationId={mobileConvoId}
              minimised={false}
              onClose={handleCloseMobileActive}
              compact
              realtimeEvent={floatingRealtimeEvent}
              typingUsers={floatingTypingUsers}
              wsState={floatingWsState}
              callsApi={floatingCallsEnabled ? floatingCallsApi : null}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-theme-muted">
              No open conversation.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // DESKTOP — compact stack of up to 3 windows at preferred corner
  // ------------------------------------------------------------------
  const corner = prefs.floating_position || 'bottom-right';
  const isRight = corner.endsWith('right');
  const isTop = corner.startsWith('top');

  const containerClass = isTop
    ? isRight
      ? 'top-20 right-4'
      : 'top-20 left-4'
    : isRight
      ? 'bottom-3 right-4 pb-[env(safe-area-inset-bottom)]'
      : 'bottom-3 left-4 pb-[env(safe-area-inset-bottom)]';

  const flexClass = isRight ? 'flex-row-reverse' : 'flex-row';

  // Keep desktop controlled. The product brief says up to 3 panels;
  // more than that becomes hard to use and starts covering the app.
  const desktopWindows = windows.slice(-3);
  const hiddenWindowCount = Math.max(0, windows.length - desktopWindows.length);

  return (
    <div
      className={`fixed ${containerClass} z-[300] flex ${flexClass} items-end gap-2 pointer-events-none max-w-[calc(100dvw-2rem)]`}
      data-testid="floating-messenger-dock"
    >
      {hiddenWindowCount > 0 && (
        <button
          type="button"
          onClick={() => {
            const firstHidden = windows[0]?.id;
            if (firstHidden) openConversation(firstHidden);
          }}
          className="pointer-events-auto h-9 px-3 rounded-full bg-theme-surface border border-theme shadow-xl text-xs font-semibold text-theme-text hover:bg-theme-secondary"
          data-testid="floating-messenger-hidden-count"
          title={`${hiddenWindowCount} more open chat${hiddenWindowCount === 1 ? '' : 's'}`}
        >
          +{hiddenWindowCount}
        </button>
      )}

      {desktopWindows.map((windowItem) => (
        <div key={windowItem.id} className="pointer-events-auto">
          <ChatWindow
            conversationId={windowItem.id}
            minimised={!!windowItem.minimised}
            onMinimise={(next) => minimiseConversation(windowItem.id, next)}
            onClose={() => closeConversation(windowItem.id)}
            realtimeEvent={floatingRealtimeEvent}
            typingUsers={floatingTypingUsers}
            wsState={floatingWsState}
            callsApi={floatingCallsEnabled ? floatingCallsApi : null}
          />
        </div>
      ))}
    </div>
  );
}
